import React, { useState, useEffect } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Sun, 
  Calculator, 
  Euro,
  TrendingUp,
  Zap,
  Home,
  Calendar,
  CheckCircle,
  FileText,
  UserPlus,
  Download
} from "lucide-react";
import { toast } from "sonner";
import { calculateSolarFinancials, estimateInstallationCost, calculateSubsidies } from "../utils/solarCalculations";
import { generateFullPDF } from "../utils/generateFullPDF";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SimulatorPageEnhanced = () => {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  const [newClient, setNewClient] = useState({
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    adresse: "",
    code_postal: "",
    ville: "",
    revenu_fiscal_reference: "",
    telephone_portable: "",  // Ajouté pour correspondre au modèle
  });
  
  const [formData, setFormData] = useState({
    installationPowerKwc: "",
    installationCost: "",
    subsidies: "",
    electricityPrice: "0.2016",
    revenuFiscal: "",
    projectLifetime: "25",
  });
  
  const [results, setResults] = useState(null);
  const [autoCalculate, setAutoCalculate] = useState(true);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (selectedClientId) {
      const client = clients.find(c => c.id === selectedClientId);
      setSelectedClient(client);
      
      if (formData.installationPowerKwc && autoCalculate) {
        const power = parseFloat(formData.installationPowerKwc);
        const cost = estimateInstallationCost(power);
        const revenu = parseFloat(client?.revenu_fiscal_reference || formData.revenuFiscal || 0);
        const subsidies = calculateSubsidies(power, revenu);
        
        setFormData(prev => ({
          ...prev,
          installationCost: cost.toFixed(0),
          subsidies: subsidies.toFixed(0),
          revenuFiscal: client?.revenu_fiscal_reference?.toString() || prev.revenuFiscal,
        }));
      }
    }
  }, [selectedClientId, clients]);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClients(response.data);
    } catch (error) {
      console.error("Erreur:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    if (name === "installationPowerKwc" && autoCalculate && value) {
      const power = parseFloat(value);
      if (!isNaN(power) && power > 0) {
        const cost = estimateInstallationCost(power);
        const revenu = parseFloat(formData.revenuFiscal || 0);
        const subsidies = calculateSubsidies(power, revenu);
        
        setFormData(prev => ({
          ...prev,
          installationCost: cost.toFixed(0),
          subsidies: subsidies.toFixed(0),
        }));
      }
    }
  };

  const handleCreateClient = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // Créer l'objet client conforme au modèle backend
      const clientData = {
        prenom: newClient.prenom,
        nom: newClient.nom,
        email: newClient.email || undefined,
        telephone_portable: newClient.telephone || undefined,
        adresse: newClient.adresse || "",
        code_postal: newClient.code_postal || "",
        ville: newClient.ville || "",
        revenu_fiscal_reference: newClient.revenu_fiscal_reference ? parseFloat(newClient.revenu_fiscal_reference) : undefined,
        status: "prospect",
      };
      
      const response = await axios.post(`${API}/clients`, clientData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Client créé avec succès !");
      setShowClientDialog(false);
      fetchClients();
      setSelectedClientId(response.data.id);
      setNewClient({
        prenom: "",
        nom: "",
        email: "",
        telephone: "",
        adresse: "",
        code_postal: "",
        ville: "",
        revenu_fiscal_reference: "",
        telephone_portable: "",
      });
    } catch (error) {
      console.error("Erreur:", error);
      // Extraire le message d'erreur correctement
      let errorMessage = "Erreur lors de la création du client";
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Erreur de validation Pydantic
          errorMessage = error.response.data.detail.map(err => err.msg).join(", ");
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleCalculate = () => {
    const power = parseFloat(formData.installationPowerKwc);
    const cost = parseFloat(formData.installationCost);
    const subsidies = parseFloat(formData.subsidies);
    const electricityPrice = parseFloat(formData.electricityPrice);
    const projectLifetime = parseInt(formData.projectLifetime);
    
    if (!power || !cost) {
      toast.error("Veuillez remplir au moins la puissance et le coût d'installation");
      return;
    }
    
    const calculationParams = {
      installationPowerKwc: power,
      installationCostEuros: cost,
      subsidiesEuros: subsidies || 0,
      currentElectricityPricePerKwh: electricityPrice,
      projectLifetimeYears: projectLifetime,
      location: selectedClient ? {
        address: `${selectedClient.adresse || ''} ${selectedClient.code_postal || ''} ${selectedClient.ville || ''}`
      } : undefined,
    };
    
    const calculatedResults = calculateSolarFinancials(calculationParams);
    setResults(calculatedResults);
    toast.success("Calculs effectués !");
  };

  const generatePDF = async () => {
    if (!results) {
      toast.error("Veuillez d'abord calculer les résultats");
      return;
    }

    setIsGeneratingPDF(true);

    try {
      const clientName = selectedClient ? `${selectedClient.prenom} ${selectedClient.nom}` : 'Client';
      const clientAddress = selectedClient ? `${selectedClient.adresse}, ${selectedClient.code_postal} ${selectedClient.ville}` : '';
      const clientPhone = selectedClient ? selectedClient.telephone_portable || selectedClient.telephone || '' : '';
      
      const simulationData = {
        clientName,
        clientAddress,
        clientPhone,
        panelCount: Math.round(parseFloat(formData.installationPowerKwc) / 0.5),
        panelPower: 500,
        totalPower: parseFloat(formData.installationPowerKwc),
        installationCost: parseFloat(formData.installationCost),
        subsidies: parseFloat(formData.subsidies) || 0,
        annualProduction: results.annualProductionKwh,
        annualSavings: results.annualSavingsEuros,
        roiYears: results.paybackPeriodYears,
        netCost: results.netInstallationCost,
        productionPerKwc: results.productionPerKwc,
        roi20Years: results.roi20Years,
        cumulativeSavings20Years: results.cumulativeSavings20Years,
        netProfitability20Years: results.netProfitability20Years,
        yearlyBreakdown: results.yearlyBreakdown || []
      };

      await generateFullPDF(simulationData);
      toast.success("PDF généré avec succès !");
    } catch (error) {
      console.error("Erreur génération PDF:", error);
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleSaveAsProjet = async () => {
    if (!selectedClientId) {
      toast.error("Veuillez sélectionner un client");
      return;
    }
    
    if (!results) {
      toast.error("Veuillez d'abord calculer les résultats");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      
      const projetData = {
        client_id: selectedClientId,
        type_projet: "photovoltaique",
        titre: `Installation ${formData.installationPowerKwc} kWc`,
        description: `Production annuelle estimée: ${results.annualProductionKwh.toFixed(0)} kWh\nRetour sur investissement: ${results.paybackPeriodYears} ans`,
        adresse_installation: selectedClient.adresse || "",
        code_postal: selectedClient.code_postal || "",
        ville: selectedClient.ville || "",
        puissance_kwc: parseFloat(formData.installationPowerKwc),
        nombre_panneaux: Math.round(parseFloat(formData.installationPowerKwc) / 0.5),
        montant_total_ht: parseFloat(formData.installationCost),
        aides_cee_montant: parseFloat(formData.subsidies) || 0,
        total_aides: parseFloat(formData.subsidies) || 0,
        reste_a_charge: results.netInstallationCost,
        statut: "devis",
        produits: [],
      };
      
      await axios.post(`${API}/projets`, projetData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success("Projet créé avec succès !");
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la création du projet");
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Sun className="w-8 h-8 text-yellow-500" />
            Simulateur Solaire SUNLIFE
          </h1>
          <p className="text-slate-600 mt-1">Calculez la rentabilité d'une installation photovoltaïque</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulaire */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="w-5 h-5" />
                Client
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Sélectionner un client</Label>
                  <select
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg mt-1"
                  >
                    <option value="">-- Nouveau client --</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.prenom} {c.nom} - {c.ville || 'Ville inconnue'}
                      </option>
                    ))}
                  </select>
                </div>
                
                <Button 
                  onClick={() => setShowClientDialog(true)}
                  variant="outline"
                  className="w-full"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Créer un nouveau client
                </Button>
                
                {selectedClient && (
                  <div className="bg-slate-50 p-3 rounded-lg text-sm">
                    <p><strong>Adresse:</strong> {selectedClient.adresse}</p>
                    <p><strong>CP:</strong> {selectedClient.code_postal} <strong>Ville:</strong> {selectedClient.ville}</p>
                    {selectedClient.revenu_fiscal_reference && (
                      <p><strong>Revenu Fiscal:</strong> {selectedClient.revenu_fiscal_reference}€</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Paramètres de l'Installation
              </CardTitle>
              <CardDescription>
                {autoCalculate && (
                  <div className="flex items-center gap-2 text-xs text-green-600 mt-2">
                    <CheckCircle className="w-3 h-3" />
                    Calcul automatique des coûts activé
                  </div>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Puissance installation (kWc) *</Label>
                  <Input
                    type="number"
                    name="installationPowerKwc"
                    value={formData.installationPowerKwc}
                    onChange={handleInputChange}
                    placeholder="Ex: 6"
                    step="0.5"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Nombre de panneaux estimé: {Math.round(parseFloat(formData.installationPowerKwc || 0) / 0.5)} (500W/panneau)
                  </p>
                </div>

                <div>
                  <Label>Coût d'installation (€ HT) *</Label>
                  <Input
                    type="number"
                    name="installationCost"
                    value={formData.installationCost}
                    onChange={handleInputChange}
                    placeholder="Ex: 15000"
                  />
                </div>

                <div>
                  <Label>Aides financières (€)</Label>
                  <Input
                    type="number"
                    name="subsidies"
                    value={formData.subsidies}
                    onChange={handleInputChange}
                    placeholder="CEE + MaPrimeRénov"
                  />
                </div>

                <div>
                  <Label>Revenu Fiscal Référence (€)</Label>
                  <Input
                    type="number"
                    name="revenuFiscal"
                    value={formData.revenuFiscal}
                    onChange={handleInputChange}
                    placeholder="Pour calcul MaPrimeRénov"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Prix électricité (€/kWh)</Label>
                    <Input
                      type="number"
                      name="electricityPrice"
                      value={formData.electricityPrice}
                      onChange={handleInputChange}
                      step="0.001"
                    />
                  </div>
                  <div>
                    <Label>Durée projet (années)</Label>
                    <Input
                      type="number"
                      name="projectLifetime"
                      value={formData.projectLifetime}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button 
              onClick={handleCalculate}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              <Calculator className="w-4 h-4 mr-2" />
              Calculer
            </Button>
          </div>
        </div>

        {/* Résultats */}
        <div className="space-y-6">
          {results ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-gradient-to-br from-green-50 to-green-100">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <TrendingUp className="w-8 h-8 mx-auto text-green-600 mb-2" />
                      <div className="text-3xl font-bold text-green-700">
                        {results.roi20Years.toFixed(1)}%
                      </div>
                      <p className="text-sm text-green-600 mt-1">ROI sur 20 ans</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <Calendar className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                      <div className="text-3xl font-bold text-blue-700">
                        {results.paybackPeriodYears}
                      </div>
                      <p className="text-sm text-blue-600 mt-1">Années avant retour</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    Production Solaire
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Production annuelle</span>
                      <span className="font-bold">{results.annualProductionKwh.toFixed(0)} kWh/an</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Rendement</span>
                      <span className="font-bold">{results.productionPerKwc} kWh/kWc/an</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Euro className="w-5 h-5 text-green-600" />
                    Analyse Financière
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Coût net (après aides)</span>
                      <span className="font-bold">{results.netInstallationCost.toFixed(0)}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Économies an 1</span>
                      <span className="font-bold text-green-600">{results.annualSavingsEuros.toFixed(0)}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Économies 20 ans</span>
                      <span className="font-bold text-green-600">{results.cumulativeSavings20Years.toFixed(0)}€</span>
                    </div>
                    <div className="flex justify-between border-t pt-3">
                      <span className="text-slate-600 font-medium">Bénéfice net 20 ans</span>
                      <span className="font-bold text-lg text-green-700">
                        {results.netProfitability20Years.toFixed(0)}€
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button 
                  onClick={generatePDF}
                  disabled={isGeneratingPDF}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isGeneratingPDF ? "Génération..." : "Télécharger PDF"}
                </Button>
                {selectedClientId && (
                  <Button 
                    onClick={handleSaveAsProjet}
                    variant="outline"
                    className="flex-1"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Créer le Projet
                  </Button>
                )}
              </div>
            </>
          ) : (
            <Card className="h-full flex items-center justify-center">
              <CardContent className="text-center py-12">
                <Sun className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">
                  Remplissez le formulaire et cliquez sur "Calculer"
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Dialog Nouveau Client */}
      <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Créer un nouveau client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom *</Label>
                <Input
                  value={newClient.prenom}
                  onChange={(e) => setNewClient({...newClient, prenom: e.target.value})}
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  value={newClient.nom}
                  onChange={(e) => setNewClient({...newClient, nom: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({...newClient, email: e.target.value})}
              />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input
                value={newClient.telephone}
                onChange={(e) => setNewClient({...newClient, telephone: e.target.value})}
              />
            </div>
            <div>
              <Label>Adresse</Label>
              <Input
                value={newClient.adresse}
                onChange={(e) => setNewClient({...newClient, adresse: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Code Postal</Label>
                <Input
                  value={newClient.code_postal}
                  onChange={(e) => setNewClient({...newClient, code_postal: e.target.value})}
                />
              </div>
              <div>
                <Label>Ville</Label>
                <Input
                  value={newClient.ville}
                  onChange={(e) => setNewClient({...newClient, ville: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Revenu Fiscal Référence (€)</Label>
              <Input
                type="number"
                value={newClient.revenu_fiscal_reference}
                onChange={(e) => setNewClient({...newClient, revenu_fiscal_reference: e.target.value})}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowClientDialog(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleCreateClient}
                className="bg-red-600 hover:bg-red-700"
                disabled={!newClient.prenom || !newClient.nom}
              >
                Créer le client
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SimulatorPageEnhanced;